import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';

import {
  OverdueInvoiceDebtDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ValidationAlert from '../../../app/components/ValidationAlert';
import momencik from '../../../common/momencik';
import numberFormatter from '../../../common/numberFormatter';
import mathUtils from '../../../utils/mathUtils';
import { useNavigation } from '../../../common/navigation';

import { CompanyRankingList } from './SuperAdminCompanyRankingList';

export const SuperAdminOverdueTab: React.FC = () => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const [data, setData] = useState<OverdueInvoiceDebtDto[]>([]);
  const [unlinkedTotal, setUnlinkedTotal] = useState<string>('0');
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    Promise.all([
      spd.reports.reportOverdueInvoices(),
      spd.reports.reportUnlinkedPaymentsTotal(),
    ])
      .then(([overdueResult, unlinkedResult]) => {
        setData(overdueResult);
        setUnlinkedTotal(unlinkedResult.total ?? '0');
        setLoading(false);
      })
      .catch(async error => {
        console.error('Error loading overdue invoices', error);
        setErrors(await responseErrors(error as ResponseError));
        setLoading(false);
      });
  }, [spd.reports]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <Spinner animation="border" variant="danger" />
      </div>
    );
  }

  if (errors.length > 0) {
    return <ValidationAlert show={true} errors={errors} className="mt-3" />;
  }

  if (data.length === 0) {
    return (
      <div className="text-center text-muted mt-5">
        <h5>Brak przeterminowanych faktur</h5>
      </div>
    );
  }

  const total = data.reduce(
    (sum, row) => mathUtils.add(sum, row.totalOverdue || '0'),
    0,
  );

  return (
    <div>
      <div className="border rounded-3 p-3 mb-4 d-flex align-items-baseline gap-2 bg-light">
        <span className="text-muted">
          Kwota niedowiązanych wpłat w systemie:
        </span>
        <strong className="text-warning fs-5">
          {numberFormatter(unlinkedTotal, true)}
        </strong>
      </div>
      <div className="d-flex align-items-baseline gap-3 mb-4">
        <h5 className="mb-0">Przeterminowane należności wg firm</h5>
        <span className="text-muted">
          Łącznie:{' '}
          <strong className="text-danger">
            {numberFormatter(total.toString(), true)}
          </strong>
          {' · '}
          {data.length}{' '}
          {data.length === 1 ? 'firma' : data.length < 5 ? 'firmy' : 'firm'}
        </span>
      </div>
      <CompanyRankingList
        items={data}
        emptyText="Brak przeterminowanych faktur"
        cardClassName="overdue-card"
        metaClassName="overdue-card-meta"
        badgeClassName="company-ranking-badge company-ranking-badge--overdue"
        badgeBg="danger"
        onItemClick={item =>
          nav.companyOverdueInvoices(item.companyId, item.companyName, true)
        }
        getBadgeText={item => numberFormatter(item.totalOverdue, true)}
        getMetaText={item => {
          const count = item.invoiceCount || 0;

          return (
            <>
              <span>
                {count}{' '}
                {count === 1 ? 'faktura' : count < 5 ? 'faktury' : 'faktur'} po
                terminie
              </span>
              {item.oldestDueDate && (
                <span className="ms-2">
                  · najstarsza:{' '}
                  <span className="text-danger">
                    {momencik(item.oldestDueDate, 'YYYY-MM-DD')}
                  </span>
                </span>
              )}
            </>
          );
        }}
      />
    </div>
  );
};
